import http from '../../../services/api'
import type { APIPaginatedResponse, Pagination } from '../../../services/types'
import { getRequestParamString } from '../../../services/utils/utils'
import type { ImportJobApi, ImportPreviewApi, ImportProgressApi } from '../types'
import { toImportTypeSlug } from '../utils'

const apiPath = import.meta.env.VITE_API_PATH

type ImportJobSingleResponse = { data: ImportJobApi }
type ImportPreviewSingleResponse = { data: ImportPreviewApi }
type ImportProgressSingleResponse = { data: ImportProgressApi }

async function getImports(pagination?: Partial<Pagination>) {
  const paramString = getRequestParamString(undefined, pagination)
  return await http.get<APIPaginatedResponse<ImportJobApi>>(`${apiPath}imports${paramString}`)
}

async function getImport(uuid: string) {
  return await http.get<ImportJobSingleResponse>(`${apiPath}imports/${uuid}`)
}

async function uploadImportFile(file: File, importType: string) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('import_type', importType)

  return await http.post<ImportJobSingleResponse>(`${apiPath}imports/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

async function getImportPreview(uuid: string, pagination?: { page?: number; per_page?: number }) {
  const params = new URLSearchParams()
  if (pagination?.page !== undefined) {
    params.set('page', String(pagination.page))
  }
  if (pagination?.per_page !== undefined) {
    params.set('per_page', String(pagination.per_page))
  }

  const query = params.toString()
  const suffix = query ? `?${query}` : ''

  return await http.get<ImportPreviewSingleResponse>(`${apiPath}imports/${uuid}/preview${suffix}`)
}

async function getImportProgress(uuid: string) {
  return await http.get<ImportProgressSingleResponse>(`${apiPath}imports/${uuid}/progress`)
}

async function processImport(uuid: string, duplicateHandlingStrategy: number) {
  return await http.post<ImportJobSingleResponse>(`${apiPath}imports/${uuid}/process`, {
    duplicate_handling_strategy: duplicateHandlingStrategy,
  })
}

async function downloadTemplate(importType: string) {
  const slug = toImportTypeSlug(importType)
  return await http.get<Blob>(`${apiPath}imports/templates/${slug}`, {
    responseType: 'blob',
  })
}

const importsService = {
  getImports,
  getImport,
  uploadImportFile,
  getImportPreview,
  getImportProgress,
  processImport,
  downloadTemplate,
}

export default importsService
